import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";

// Cycle 1 began on this Thursday, Taipei time. Mirrors _week_of in
// backend/domains/usageBilling/usageBilling_services.py. The backend dates
// every week it returns; this is only needed to label the weeks it keeps no
// row for (a week without AI use) and to number cycles.
export const CYCLE_ANCHOR = "2026-10-08";
const CYCLE_DAYS = 28;

// ─── Money ────────────────────────────────────────────────────────────────

const UNDER_ONE_DOLLAR = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const ONE_DOLLAR_OR_MORE = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// The one rule for every USD amount on the usage screens. A turn often costs
// under a cent, so amounts under $1 keep up to 4 decimals ($0.0174, $0.50);
// $1 and up get the usual 2. null means "no limit" to the backend - wording
// that is the caller's, so it only gets a dash here.
export function formatUsd(value) {
  if (value === null || value === undefined) return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  if (amount > 0 && amount < 0.0001) return "<$0.0001";
  return Math.abs(amount) < 1
    ? UNDER_ONE_DOLLAR.format(amount)
    : ONE_DOLLAR_OR_MORE.format(amount);
}

// The modal's currency switch needs an explicit code on every USD amount.
export function formatUsdWithCode(value) {
  const amount = formatUsd(value);
  return amount === "—" ? amount : `${amount} USD`;
}

const TWD_UNDER_ONE = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "TWD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const TWD_ONE_OR_MORE = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "TWD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Convert only at display time, before rounding; null still means no limit.
// Small AI costs keep enough decimals to avoid appearing free.
export function formatTwdFromUsd(value, rate) {
  if (value === null || value === undefined) return "—";
  if (!Number.isFinite(rate) || rate <= 0) return "—";
  const amount = Number(value) * rate;
  if (!Number.isFinite(amount)) return "—";
  if (amount > 0 && amount < 0.0001) return "<NT$0.0001";
  return Math.abs(amount) < 1
    ? TWD_UNDER_ONE.format(amount)
    : TWD_ONE_OR_MORE.format(amount);
}

// A monthly limit typed into an admin form: a dollar amount >= 0 with at most
// 2 decimals. Returns the number, or null when the text isn't one.
export function parseLimitInput(text) {
  const trimmed = String(text ?? "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const amount = Number(trimmed);
  // Never let an overflowing number become JSON null (which removes a limit).
  return Number.isFinite(amount) && Number.isSafeInteger(Math.round(amount * 100))
    ? amount
    : null;
}

// Limit entry is a one-time conversion. The exact USD cents returned here
// are both previewed and sent to the existing USD-only limit endpoint.
export function limitAmountToUsd(text, currency, rate) {
  const amount = parseLimitInput(text);
  if (amount === null) return null;
  if (currency === "USD") return amount;
  if (currency !== "TWD" || !Number.isFinite(rate) || rate <= 0) return null;
  const usd = amount / rate;
  const cents = Math.round((usd + Number.EPSILON * Math.max(1, usd)) * 100);
  return Number.isSafeInteger(cents) ? cents / 100 : null;
}

// Share of a quota used, as a whole percent. null when there is no quota. A
// quota of 0 counts as fully used, the same as the backend's over_quota rule
// (used >= quota).
export function percentUsed(used, quota) {
  if (quota === null || quota === undefined) return null;
  if (quota <= 0) return 100;
  return Math.round((used / quota) * 100);
}

// ─── Dates ────────────────────────────────────────────────────────────────
// The backend sends plain YYYY-MM-DD dates. parseISO reads them as local
// midnight; new Date("2026-10-15") would read UTC midnight and show the
// previous day anywhere west of UTC.

const toIso = (date) => format(date, "yyyy-MM-dd");

// Spelled out rather than taken from date-fns/locale, whose ESM-only entry
// the react-scripts Jest can't load. Indexed by getDay(), Sunday first.
const ZH_WEEKDAYS = "日一二三四五六";

// "Thu 22 Oct" / "10月22日（週四）"
export function formatDay(iso, language) {
  const day = parseISO(iso);
  return language === "zh"
    ? `${format(day, "M月d日")}（週${ZH_WEEKDAYS[day.getDay()]}）`
    : format(day, "EEE d MMM");
}

// "22 Oct" / "10/22" - axis ticks and cycle ranges
export function formatShortDay(iso, language) {
  return format(parseISO(iso), language === "zh" ? "M/d" : "d MMM");
}

// "Thu 15 – Wed 21 Oct" / "10/15 – 10/21"
export function formatWeekRange(start, end, language) {
  const first = parseISO(start);
  const last = parseISO(end);
  if (language === "zh") return `${format(first, "M/d")} – ${format(last, "M/d")}`;
  const sameMonth = first.getMonth() === last.getMonth();
  return `${format(first, sameMonth ? "EEE d" : "EEE d MMM")} – ${format(last, "EEE d MMM")}`;
}

// A week ends on a Wednesday; the next one starts, and the quota resets, the
// day after - at 00:00 Taipei time.
export function resetDay(weekEnd) {
  return toIso(addDays(parseISO(weekEnd), 1));
}

// 1 for the cycle that began on CYCLE_ANCHOR, 2 for the next, ...
export function cycleNumber(cycleStart) {
  return (
    differenceInCalendarDays(parseISO(cycleStart), parseISO(CYCLE_ANCHOR)) /
      CYCLE_DAYS +
    1
  );
}

// Every cycle so far, newest first, as {start, end}: the current one (from the
// backend) back to cycle 1. There is nothing before cycle 1.
export function cyclesUpTo(currentCycleStart) {
  const cycles = [];
  for (let n = cycleNumber(currentCycleStart); n >= 1; n--) {
    const start = addDays(parseISO(CYCLE_ANCHOR), (n - 1) * CYCLE_DAYS);
    cycles.push({
      start: toIso(start),
      end: toIso(addDays(start, CYCLE_DAYS - 1)),
    });
  }
  return cycles;
}

// ─── Wording ──────────────────────────────────────────────────────────────

const LIMIT_SOURCE = {
  group: { en: "Your group's limit", zh: "您群組的額度" },
  user: { en: "Set for you by an admin", zh: "管理員為您設定的額度" },
  user_month: {
    en: "Set for you for this cycle only",
    zh: "管理員僅為本週期設定的額度",
  },
};

// Which limit applies to the signed-in user, from the backend's limit_source.
export function limitSourceLabel(source, language) {
  const label = LIMIT_SOURCE[source] || { en: "No limit set", zh: "未設定額度" };
  return language === "zh" ? label.zh : label.en;
}

const ADMIN_LIMIT_SOURCE = {
  group: { en: "Group limit", zh: "群組額度" },
  user: { en: "Override · permanent", zh: "個人額度 · 永久" },
  user_month: { en: "Override · this cycle only", zh: "個人額度 · 僅本週期" },
};

// The same, worded for an admin looking at someone else.
export function adminLimitSourceLabel(source, language) {
  const label = ADMIN_LIMIT_SOURCE[source] || { en: "No limit", zh: "未設定額度" };
  return language === "zh" ? label.zh : label.en;
}

// Why a call went unpriced - the reason codes in
// documentation/root_agent_usage_billing_api.md. An unknown code shows as-is.
const UNPRICED_REASON = {
  usage_not_available: {
    en: "Usage not available where the call runs",
    zh: "呼叫執行處無法取得用量",
  },
  provider_timeout: { en: "Provider call timed out", zh: "供應商呼叫逾時" },
  downstream_timeout: {
    en: "Downstream service timed out",
    zh: "下游服務逾時",
  },
  downstream_not_reporting: {
    en: "Downstream service doesn't report usage yet",
    zh: "下游服務尚未回報用量",
  },
  model_not_in_price_table: {
    en: "Model not in the price table",
    zh: "價目表中沒有此模型",
  },
  invalid_usage: { en: "Invalid token counts", zh: "Token 數量無效" },
  unspecified: { en: "No reason given", zh: "未提供原因" },
};

export function unpricedReasonLabel(reason, language) {
  const label = UNPRICED_REASON[reason];
  if (!label) return reason;
  return language === "zh" ? label.zh : label.en;
}

// ─── Weekly history ───────────────────────────────────────────────────────

// A week the backend keeps no row for, because it had no AI use. Its quota
// was never worked out, so it stays null.
function emptyWeek(start) {
  const sinceAnchor = differenceInCalendarDays(start, parseISO(CYCLE_ANCHOR));
  const intoCycle = sinceAnchor % CYCLE_DAYS;
  return {
    cycle_start: toIso(addDays(start, -intoCycle)),
    week_no: Math.floor(intoCycle / 7) + 1,
    week_start: toIso(start),
    week_end: toIso(addDays(start, 6)),
    this_week_quota_usd: null,
    used_usd: 0,
    turns: 0,
    over_quota: false,
  };
}

// The weeks to chart, oldest first: up to `count` finished weeks before the
// current one, never reaching back past cycle 1, then the current week
// (flagged isCurrent). `history` is the backend's list of finished weeks,
// newest first, which skips weeks without AI use - those are filled in at $0.
export function fillWeeks(current, history = [], count = 8) {
  const byStart = new Map(history.map((week) => [week.week_start, week]));
  const currentStart = parseISO(current.week_start);
  const weeksSinceAnchor = Math.floor(
    differenceInCalendarDays(currentStart, parseISO(CYCLE_ANCHOR)) / 7,
  );
  const weeks = [];
  for (let back = Math.min(count, weeksSinceAnchor); back >= 1; back--) {
    const start = addDays(currentStart, -7 * back);
    weeks.push({
      ...(byStart.get(toIso(start)) || emptyWeek(start)),
      isCurrent: false,
    });
  }
  weeks.push({ ...current, isCurrent: true });
  return weeks;
}
